import React, { useMemo, useState } from "react";
import SectionCard from "../ui/SectionCard";
import NumberField from "../ui/NumberField";
import RangeField from "../ui/RangeField";

const FALLBACK_WEATHER_DAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6].map(index => ({
  index,
  label: `Day ${index + 1}`
}));

const FilterPanel = ({
  showFilters,
  filters,
  visibleBothiesCount,
  allBothiesCount,
  availableBothyFeatures,
  availableBortleClasses,
  naturalFeatureOptions,
  weatherDayOptions = [],
  weatherUnits,
  weatherRanges,
  onBothyFeatureToggle,
  onNaturalFeatureToggle,
  onElevationChange,
  onRoadDistanceChange,
  onBortleClassChange,
  onWeatherFilterChange,
  onClearFilters,
  onToggleFilters
}) => {
  const [openSections, setOpenSections] = useState({
    bothyFeatures: false,
    natural: false,
    sky: false,
    elevation: false,
    weather: false
  });

  const toggleSection = key => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const parseValue = value => (value === "" ? "" : Number(value));

  const activeBothyFeatures = filters.bothyFeatures.length;
  const activeNatural = Object.values(filters.naturalFeatures).filter(Boolean)
    .length;
  const activeSky =
    filters.bortleClass === "" || filters.bortleClass === null ? 0 : 1;
  const activeElevation = [filters.elevation.min, filters.elevation.max].filter(
    v => v !== "" && v !== null && v !== undefined
  ).length;
  const activeRoad = [
    filters.roadDistance.min,
    filters.roadDistance.max
  ].filter(v => v !== "" && v !== null && v !== undefined).length;
  const activeWeather = Object.values(filters.weather).filter(
    value => value !== "" && value !== null && value !== undefined
  ).length;
  const totalActive =
    activeBothyFeatures +
    activeNatural +
    activeSky +
    activeElevation +
    activeRoad +
    activeWeather;

  const makeMeta = count => `${count}`;

  const formattedWeatherDayOptions = useMemo(
    () => {
      if (weatherDayOptions.length > 0) {
        return weatherDayOptions.map(option => ({
          index: option.index,
          label: formatWeatherDayLabel(option.isoDate)
        }));
      }
      return FALLBACK_WEATHER_DAY_OPTIONS;
    },
    [weatherDayOptions]
  );

  const dailyUnits = weatherUnits?.daily ?? {};
  const resolveUnit = (unit, fallback) => unit || fallback;
  const unitToScale = (unit, defaults) => {
    const lower = (unit || "").toLowerCase();
    if (lower === "mm")
      return { unitLabel: "mm", step: defaults.mmStep, max: defaults.mmMax };
    if (lower === "cm")
      return { unitLabel: "cm", step: defaults.cmStep, max: defaults.cmMax };
    if (lower === "m")
      return { unitLabel: "m", step: defaults.mStep, max: defaults.mMax };
    return {
      unitLabel: defaults.fallbackUnit,
      step: defaults.fallbackStep,
      max: defaults.fallbackMax
    };
  };

  const depthScale = useMemo(
    () => {
      const lower = (dailyUnits.snow_depth_max || "").toLowerCase();
      if (lower === "m") return { unitLabel: "mm", step: 10, max: 3000 };
      if (lower === "cm") return { unitLabel: "mm", step: 10, max: 3000 };
      if (lower === "mm") return { unitLabel: "mm", step: 10, max: 3000 };
      return {
        unitLabel: dailyUnits.snow_depth_max || "mm",
        step: 10,
        max: 3000
      };
    },
    [dailyUnits.snow_depth_max]
  );

  const weatherRangeFields = useMemo(
    () => {
      const windUnit = resolveUnit(dailyUnits.wind_speed_10m_max, "km/h");
      const windLower = windUnit.toLowerCase();
      return [
        (() => {
          const scale = unitToScale(dailyUnits.precipitation_sum, {
            mmStep: 1,
            mmMax: 100,
            cmStep: 0.5,
            cmMax: 10,
            mStep: 0.1,
            mMax: 1,
            fallbackUnit: resolveUnit(dailyUnits.precipitation_sum, "units"),
            fallbackStep: 1,
            fallbackMax: 100
          });
          return {
            id: "weather-max-precip",
            label: `Max precipitation (${scale.unitLabel})`,
            field: "maxPrecipitation",
            min: 0,
            min: weatherRanges?.precipitation?.range?.min ?? 0,
            max: weatherRanges?.precipitation?.range?.max ?? scale.max,
            step: scale.step,
            unit: scale.unitLabel
          };
        })(),
        (() => {
          const scale = unitToScale(dailyUnits.rain_sum, {
            mmStep: 1,
            mmMax: 100,
            cmStep: 0.5,
            cmMax: 10,
            mStep: 0.1,
            mMax: 1,
            fallbackUnit: resolveUnit(dailyUnits.rain_sum, "units"),
            fallbackStep: 1,
            fallbackMax: 100
          });
          return {
            id: "weather-max-rain",
            label: `Max rain (${scale.unitLabel})`,
            field: "maxRainfall",
            min: 0,
            min: weatherRanges?.rain?.range?.min ?? 0,
            max: weatherRanges?.rain?.range?.max ?? scale.max,
            step: scale.step,
            unit: scale.unitLabel
          };
        })(),
        (() => {
          const scale = unitToScale(dailyUnits.snowfall_sum, {
            mmStep: 1,
            mmMax: 200,
            cmStep: 0.5,
            cmMax: 200,
            mStep: 0.05,
            mMax: 2,
            fallbackUnit: resolveUnit(dailyUnits.snowfall_sum, "units"),
            fallbackStep: 1,
            fallbackMax: 200
          });
          return {
            id: "weather-max-snow",
            label: `Max snowfall (${scale.unitLabel})`,
            field: "maxSnowfall",
            min: 0,
            min: weatherRanges?.snowfall?.range?.min ?? 0,
            max: weatherRanges?.snowfall?.range?.max ?? scale.max,
            step: scale.step,
            unit: scale.unitLabel
          };
        })(),
        (() => {
          const scale = unitToScale(dailyUnits.snowfall_sum, {
            mmStep: 1,
            mmMax: 200,
            cmStep: 0.5,
            cmMax: 200,
            mStep: 0.05,
            mMax: 2,
            fallbackUnit: resolveUnit(dailyUnits.snowfall_sum, "units"),
            fallbackStep: 1,
            fallbackMax: 200
          });
          return {
            id: "weather-min-snow",
            label: `Min snowfall (${scale.unitLabel})`,
            field: "minSnowfall",
            min: 0,
            min: weatherRanges?.snowfall?.range?.min ?? 0,
            max: weatherRanges?.snowfall?.range?.max ?? scale.max,
            step: scale.step,
            unit: scale.unitLabel
          };
        })(),
        {
          id: "weather-min-snow-depth",
          label: `Min snow depth (${depthScale.unitLabel})`,
          field: "minSnowDepth",
          min: weatherRanges?.snowDepth?.range?.min ?? 0,
          max: weatherRanges?.snowDepth?.range?.max ?? depthScale.max,
          step: depthScale.step,
          unit: depthScale.unitLabel
        },
        {
          id: "weather-max-wind",
          label: `Max wind (${windUnit})`,
          field: "maxWindSpeed",
          min: weatherRanges?.wind?.range?.min ?? 0,
          max:
            weatherRanges?.wind?.range?.max ?? (windLower === "m/s" ? 35 : 120),
          step: windLower === "m/s" ? 0.5 : 1,
          unit: windUnit
        }
      ];
    },
    [
      dailyUnits.precipitation_sum,
      dailyUnits.rain_sum,
      dailyUnits.snowfall_sum,
      dailyUnits.snow_depth_max,
      dailyUnits.wind_speed_10m_max,
      depthScale,
      weatherRanges
    ]
  );

  const weatherNumberFields = useMemo(
    () => [
      {
        id: "weather-max-precip-hours",
        label: "Max precip hours",
        field: "maxPrecipitationHours",
        min: 0
      },
      {
        id: "weather-min-low",
        label: "Min daily low (°C)",
        field: "minDailyLow"
      },
      {
        id: "weather-max-high",
        label: "Max daily high (°C)",
        field: "maxDailyHigh"
      }
    ],
    []
  );

  const renderFeatureCheckboxes = (items, selected, toggleHandler) => (
    <div className="checkbox-group">
      {items.map(item => (
        <label key={item.value || item.key || item} className="checkbox-label">
          <input
            type="checkbox"
            checked={
              Array.isArray(selected)
                ? selected.includes(item.value || item)
                : selected[item.key]
            }
            onChange={() => toggleHandler(item.value || item.key || item)}
          />
          {item.label || item}
        </label>
      ))}
    </div>
  );

  return (
    <div className="filter-panel">
      <div className="filter-panel__header" onClick={onToggleFilters}>
        <div className="filter-panel__header-actions">
          <p class="eyebrow">Filters</p>
          <span className="pill pill--border">{totalActive} active</span>
          <button
            className="clear-filters-btn"
            onClick={e => {
              e.stopPropagation();
              onClearFilters(e);
            }}
          >
            Reset filters
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filter-panel__grid">
          <SectionCard
            title="Bothy Features"
            description="Select the amenities each location must include."
            collapsible
            open={openSections.bothyFeatures}
            onToggle={() => toggleSection("bothyFeatures")}
            meta={makeMeta(activeBothyFeatures)}
          >
            {availableBothyFeatures.length > 0 ? (
              renderFeatureCheckboxes(
                availableBothyFeatures,
                filters.bothyFeatures,
                onBothyFeatureToggle
              )
            ) : (
              <span className="helper-text">
                No bothy-specific features available.
              </span>
            )}
          </SectionCard>

          <SectionCard
            title="Natural Surroundings"
            description="Highlight the type of landscape nearby."
            collapsible
            open={openSections.natural}
            onToggle={() => toggleSection("natural")}
            meta={makeMeta(activeNatural)}
          >
            {renderFeatureCheckboxes(
              naturalFeatureOptions.map(feature => ({
                key: feature.key,
                label: feature.label
              })),
              filters.naturalFeatures,
              onNaturalFeatureToggle
            )}
          </SectionCard>

          <SectionCard
            title="Sky & Light Conditions"
            description="Filter by Bortle class for darker skies."
            collapsible
            open={openSections.sky}
            onToggle={() => toggleSection("sky")}
            meta={makeMeta(activeSky)}
          >
            <select
              className="select-input"
              value={
                filters.bortleClass === "" ? "" : String(filters.bortleClass)
              }
              onChange={event => onBortleClassChange(event.target.value)}
            >
              <option value="">All classes</option>
              {availableBortleClasses.map(bortleClass => (
                <option key={bortleClass} value={bortleClass}>
                  {bortleClass}
                </option>
              ))}
            </select>
            {availableBortleClasses.length === 0 && (
              <div className="helper-text">No Bortle data available.</div>
            )}
          </SectionCard>

          <SectionCard
            title="Elevation & Access"
            description="Refine by altitude and travel distance from a road."
            collapsible
            open={openSections.elevation}
            onToggle={() => toggleSection("elevation")}
            meta={makeMeta(activeElevation + activeRoad)}
          >
            <div className="filter-panel__field-grid">
              <NumberField
                id="elevation-min"
                label="Min elevation (m)"
                value={filters.elevation.min}
                min={0}
                max={2000}
                onChange={value => onElevationChange("min", parseValue(value))}
              />
              <NumberField
                id="elevation-max"
                label="Max elevation (m)"
                value={filters.elevation.max}
                min={0}
                max={2000}
                onChange={value => onElevationChange("max", parseValue(value))}
              />
              <NumberField
                id="road-distance-min"
                label="Min road distance (km)"
                value={filters.roadDistance.min}
                min={0}
                step="0.1"
                onChange={value =>
                  onRoadDistanceChange("min", parseValue(value))
                }
              />
              <NumberField
                id="road-distance-max"
                label="Max road distance (km)"
                value={filters.roadDistance.max}
                min={0}
                step="0.1"
                onChange={value =>
                  onRoadDistanceChange("max", parseValue(value))
                }
              />
            </div>
          </SectionCard>

          <SectionCard
            title="Weather Outlook"
            description="Use cached 7-day forecasts to avoid poor conditions."
            collapsible
            open={openSections.weather}
            onToggle={() => toggleSection("weather")}
            meta={makeMeta(activeWeather)}
            actions={
              <select
                className="select-input select-input--compact"
                value={
                  filters.weather.targetDayIndex === ""
                    ? ""
                    : String(filters.weather.targetDayIndex)
                }
                onChange={event =>
                  onWeatherFilterChange(
                    "targetDayIndex",
                    event.target.value === "" ? "" : Number(event.target.value)
                  )
                }
              >
                <option value="">Any day</option>
                {formattedWeatherDayOptions.map(option => (
                  <option key={option.index} value={option.index}>
                    {option.label}
                  </option>
                ))}
              </select>
            }
          >
            <div className="filter-panel__field-grid">
              {weatherRangeFields.map(field => (
                <RangeField
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  value={filters.weather[field.field]}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  unit={field.unit}
                  onChange={value =>
                    onWeatherFilterChange(field.field, parseValue(value))
                  }
                />
              ))}
              {weatherNumberFields.map(field => (
                <NumberField
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  value={filters.weather[field.field]}
                  min={field.min}
                  step="1"
                  onChange={value =>
                    onWeatherFilterChange(field.field, parseValue(value))
                  }
                />
              ))}
            </div>
            <div className="helper-text">
              Bothies without cached forecasts are excluded when weather filters
              are active.
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;

function formatWeatherDayLabel(isoDate) {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return "Unknown date";
  }

  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short"
  });
}
